import React from 'react';
import { MessageSquare, ExternalLink, Palette } from 'lucide-react';

const FormSettings = ({ settings, onUpdate }) => {
  // Handle settings changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    onUpdate({ [name]: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <MessageSquare className="h-5 w-5 text-indigo-500 mr-2" />
          WhatsApp Integration
        </h3>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="mb-4">
            <label htmlFor="whatsappNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Your WhatsApp Number (Form Owner)
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">+</span>
              </div>
              <input
                type="tel"
                name="whatsappNumber"
                id="whatsappNumber"
                value={settings.whatsappNumber}
                onChange={handleChange}
                className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                placeholder="1234567890"
                aria-describedby="whatsapp-number-description"
              />
            </div>
            <p className="mt-2 text-sm text-gray-500" id="whatsapp-number-description">
              Enter your WhatsApp number with country code (e.g., 12025550123 for US number). Form submissions will be sent to this number.
            </p>
          </div>

          <div>
            <label htmlFor="messageTemplate" className="block text-sm font-medium text-gray-700 mb-1">
              Message Template
            </label>
            <textarea
              id="messageTemplate"
              name="messageTemplate"
              rows="4"
              value={settings.messageTemplate}
              onChange={handleChange}
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Enter message template"
              aria-describedby="message-template-description"
            ></textarea>
            <p className="mt-2 text-sm text-gray-500" id="message-template-description">
              Use <code>{'{{formData}}'}</code> to include all form fields. You can also reference specific fields with <code>{'{{fieldName}}'}</code>.
            </p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <ExternalLink className="h-5 w-5 text-indigo-500 mr-2" />
          After Submission
        </h3>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div>
            <label htmlFor="redirectUrl" className="block text-sm font-medium text-gray-700 mb-1">
              Redirect URL (Optional)
            </label>
            <input
              type="url"
              name="redirectUrl"
              id="redirectUrl"
              value={settings.redirectUrl}
              onChange={handleChange}
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="https://example.com/thank-you"
              aria-describedby="redirect-url-description"
            />
            <p className="mt-2 text-sm text-gray-500" id="redirect-url-description">
              Where to send users after form submission. Leave blank to show a default thank you message.
            </p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Palette className="h-5 w-5 text-indigo-500 mr-2" />
          Appearance
        </h3>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div>
            <label htmlFor="theme" className="block text-sm font-medium text-gray-700 mb-1">
              Form Theme
            </label>
            <select
              id="theme"
              name="theme"
              value={settings.theme}
              onChange={handleChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value="default">Default</option>
              <option value="minimal">Minimal</option>
              <option value="colorful">Colorful</option>
              <option value="dark">Dark Mode</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Important Note</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Make sure your WhatsApp number is correct and in the international format. Form submissions will be sent to this number.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormSettings;
